import * as Dialog from '@radix-ui/react-dialog'
import { Brain, Check, Eye, KeyRound, Pause, Pencil, Plus, RefreshCw, Trash2, X } from 'lucide-react'
import { useState, type FormEvent, type ReactNode } from 'react'

import type { VarlessCopyId } from '@npc/copy'
import { t } from '../../copy'
import { useAccountMembership, useProjects } from '../../app/queries'
import { AITeamNav } from '../ai-team-nav'
import { Button } from '../../components/button'
import { cn } from '../../components/cn'
import { Modal } from '../../components/modal'
import { Page, PageHeader } from '../../components/page'
import { ScreenError, ScreenLoading } from '../../components/state'
import { Tag, type TagTone } from '../../components/tag'
import {
  useCreateModelConnection,
  useDraftModelConnection,
  useCreateModelProfile,
  useDeleteModelConnection,
  useDeleteModelProfile,
  useDiscoverModels,
  useModelConnections,
  useModelProfileImpact,
  useModelProfiles,
  useModelProviders,
  useProbeModelProfile,
  useRetireModelProfile,
  useUpdateModelConnection,
  useUpdateModelProfile,
  normalizeDiscoveredModels,
  type ModelConnection,
  type ModelConfigProtocol,
  type DiscoveredModel,
  type ModelProfile,
  type ModelProvider,
} from './model-library'

const fieldClass =
  'at-input min-h-11 w-full rounded-npc-code border border-npc-border bg-npc-panel px-npc-gap-lg text-npc-body text-npc-ink outline-none placeholder:text-npc-ink-softer focus:border-npc-border-hover disabled:cursor-not-allowed disabled:text-npc-ink-softer'

const compactAction =
  'inline-flex min-h-9 items-center gap-npc-gap-xs whitespace-nowrap rounded-npc-chip px-npc-gap-sm text-npc-caption text-npc-ink-medium transition-colors hover:bg-npc-surface-warm hover:text-npc-ink disabled:cursor-not-allowed disabled:opacity-40'

type SafetyAction = { profile: ModelProfile; kind: 'disable' | 'delete' } | null

export function ModelLibraryScreen() {
  const search = new URLSearchParams(window.location.search)
  const returnTo = search.get('return_to')
  const requestedProviderID = search.get('add_provider') ?? ''
  const providers = useModelProviders()
  const connections = useModelConnections()
  const membership = useAccountMembership()
  const projects = useProjects()
  const [projectId, setProjectId] = useState('')
  const profiles = useModelProfiles(projectId || undefined)
  const [drawerProfile, setDrawerProfile] = useState<ModelProfile | null | undefined>(
    requestedProviderID ? null : undefined,
  )
  const [safetyAction, setSafetyAction] = useState<SafetyAction>(null)

  if (providers.isPending || connections.isPending || profiles.isPending || membership.isPending)
    return (
      <ScreenLoading
        shape="list"
        stats={4}
        width="workspace"
        className="ai-team-theme min-h-full bg-m-bg0 text-npc-ink"
      />
    )
  if (providers.isError) return <ScreenError error={providers.error} />
  if (connections.isError) return <ScreenError error={connections.error} />
  if (profiles.isError) return <ScreenError error={profiles.error} />

  const capabilities = membership.data?.capabilities ?? []
  const canManageConnections = capabilities.includes('vendor.manage')
  const canManageProfiles = capabilities.includes('project.manage')
  const connectionByID = new Map(
    connections.data.connections.map((connection) => [connection.id, connection] as const),
  )

  return (
    <Page className="ai-team-theme min-h-full bg-m-bg0 text-npc-ink" width="workspace">
      <AITeamNav current="/models" />
      <PageHeader
        title={t('model_configuration.model_library_title')}
        subtitle={t('model_configuration.model_library_subtitle')}
        actions={
          <div className="flex items-end gap-npc-gap-sm max-[600px]:w-full max-[600px]:items-stretch">
            <label className="grid min-w-52 gap-npc-gap-xs text-npc-meta text-npc-ink-soft max-[600px]:min-w-0 max-[600px]:flex-1">
              <span>{t('model_configuration.ui_009')}</span>
              <select
                value={projectId}
                onChange={(event) => setProjectId(event.target.value)}
                className={fieldClass}
                aria-label={t('model_configuration.ui_010')}
              >
                <option value="">{t('model_configuration.ui_011')}</option>
                {(projects.data?.items ?? []).map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name} {t('model_configuration.ui_012')}
                  </option>
                ))}
              </select>
            </label>
            {canManageConnections ? (
              <Button className="at-pill" variant="primary" onClick={() => setDrawerProfile(null)}>
                <Plus size={15} aria-hidden="true" />
                {t('model_configuration.add_model')}
              </Button>
            ) : null}
          </div>
        }
      />

      {returnTo ? (
        <div className="mt-npc-grid flex flex-wrap items-center justify-between gap-npc-gap border-y border-npc-border py-npc-grid-sm text-npc-caption text-npc-ink-medium">
          <span>{t('model_configuration.ui_014')}</span>
          <a className="font-semibold text-[color:var(--at-accent)]" href={safeReturnPath(returnTo)}>
            {t('model_configuration.ui_029')}
          </a>
        </div>
      ) : null}

      <section className="mt-npc-grid-lg" aria-labelledby="model-list-heading">
        <div className="mb-npc-grid-sm flex items-center justify-between gap-npc-gap">
          <h2 id="model-list-heading" className="m-0 text-npc-section font-semibold">
            {t('model_configuration.model_list_heading')}
          </h2>
          <span className="text-npc-caption text-npc-ink-soft">
            {t('model_configuration.model_count', {
              count: String(profiles.data.profiles.length),
            })}
          </span>
        </div>

        {profiles.data.profiles.length === 0 ? (
          <EmptyLibrary canAdd={canManageConnections} onAdd={() => setDrawerProfile(null)} />
        ) : (
          <ModelCardList
            profiles={profiles.data.profiles}
            connectionByID={connectionByID}
            canManageConnections={canManageConnections}
            canManageProfiles={canManageProfiles}
            onEdit={(profile) => setDrawerProfile(profile)}
            onSafety={(profile, kind) => setSafetyAction({ profile, kind })}
          />
        )}
      </section>

      {drawerProfile !== undefined ? (
        <ModelDrawer
          profile={drawerProfile}
          providers={providers.data.providers}
          connections={connections.data.connections}
          profiles={profiles.data.profiles}
          projectId={projectId || undefined}
          requestedProviderID={requestedProviderID}
          onClose={() => setDrawerProfile(undefined)}
        />
      ) : null}

      {safetyAction ? (
        <ProfileSafetyDialog
          action={safetyAction}
          profiles={profiles.data.profiles}
          onClose={() => setSafetyAction(null)}
        />
      ) : null}
    </Page>
  )
}

function EmptyLibrary({ canAdd, onAdd }: { canAdd: boolean; onAdd: () => void }) {
  return (
    <div className="flex min-h-72 flex-col items-center justify-center rounded-npc-card border border-npc-border bg-npc-panel px-npc-card py-npc-section text-center">
      <KeyRound size={28} aria-hidden="true" className="text-npc-ink-softer" />
      <h2 className="mb-0 mt-npc-grid-sm text-npc-section font-semibold">
        {t('model_configuration.empty_model_library')}
      </h2>
      <p className="mb-0 mt-npc-gap-xs max-w-xl text-npc-caption text-npc-ink-soft">
        {t('model_configuration.empty_model_library_hint')}
      </p>
      {canAdd ? (
        <Button className="at-pill mt-npc-grid" variant="primary" onClick={onAdd}>
          <Plus size={15} aria-hidden="true" />
          {t('model_configuration.add_model')}
        </Button>
      ) : null}
    </div>
  )
}

function ModelCardList({
  profiles,
  connectionByID,
  canManageConnections,
  canManageProfiles,
  onEdit,
  onSafety,
}: {
  profiles: readonly ModelProfile[]
  connectionByID: ReadonlyMap<string, ModelConnection>
  canManageConnections: boolean
  canManageProfiles: boolean
  onEdit: (profile: ModelProfile) => void
  onSafety: (profile: ModelProfile, kind: 'disable' | 'delete') => void
}) {
  return (
    <div className="model-library-list" data-testid="model-library-cards">
      {profiles.map((profile) => {
        const connection = connectionByID.get(profile.connection_id)
        if (!connection) return null
        const canManage = profile.project_id ? canManageProfiles : canManageConnections
        return (
          <ModelCard
            key={profile.id}
            profile={profile}
            connection={connection}
            canManage={canManage}
            onEdit={() => onEdit(profile)}
            onSafety={(kind) => onSafety(profile, kind)}
          />
        )
      })}
    </div>
  )
}

function ModelCard({
  profile,
  connection,
  canManage,
  onEdit,
  onSafety,
}: {
  profile: ModelProfile
  connection: ModelConnection
  canManage: boolean
  onEdit: () => void
  onSafety: (kind: 'disable' | 'delete') => void
}) {
  const probe = useProbeModelProfile()
  const update = useUpdateModelProfile()
  const [localError, setLocalError] = useState('')
  const busy = probe.isPending || update.isPending
  const speechConnection = profile.provider === 'volcengine_openspeech'
  const canDirectlyTest = connection.credential_present && !speechConnection
  const testModel = async () => {
    setLocalError('')
    try {
      const result = await probe.mutateAsync(profile.id)
      if (result.profile.status !== 'available') {
        setLocalError(t('model_configuration.model_saved_test_failed'))
      }
    } catch {
      setLocalError(t('model_configuration.connection_failed'))
    }
  }
  const toggle = async () => {
    setLocalError('')
    if (profile.enabled) {
      onSafety('disable')
      return
    }
    try {
      await update.mutateAsync({ profileId: profile.id, body: { enabled: true } })
    } catch {
      setLocalError(t('model_configuration.ui_052'))
    }
  }

  const providerLine = `${connection.name} · ${providerLabel(profile.provider)}`
  const keyValue = connection.credential_present
    ? connection.credential_mask || '••••••••'
    : t('model_configuration.ui_253')
  const tokensValue = `${formatTokens(profile.max_input_tokens)} · ${
    profile.project_id
      ? t('model_configuration.scope_project')
      : t('model_configuration.scope_account')
  }`
  const testLabel = speechConnection
    ? t('model_configuration.speech_not_testable')
    : !canDirectlyTest
      ? t('model_configuration.platform_auto_detected')
      : probe.isPending
        ? t('model_configuration.testing_model')
        : t('model_configuration.test_model')

  return (
    <article
      data-testid={`profile-${profile.id}`}
      className="model-library-card at-card"
    >
      <div className="mb-npc-gap-sm flex items-start justify-between gap-npc-gap">
        <ModelIdentity profile={profile} />
        <Tag className="at-pill shrink-0" tone={profileTone(profile.status)}>
          {profileLabel(profile.status)}
        </Tag>
      </div>

      <div className="model-kv mb-npc-gap-sm">
        <div>
          <div className="k">{t('model_configuration.col_provider')}</div>
          <div className="v">{providerLine}</div>
        </div>
        <div>
          <div className="k">{t('model_configuration.col_key')}</div>
          <div className="v font-npc-mono">{keyValue}</div>
        </div>
        <div>
          <div className="k">{t('model_configuration.col_url')}</div>
          <div className="v font-npc-mono" title={connection.base_url}>
            {connection.base_url}
          </div>
        </div>
        <div>
          <div className="k">{t('model_configuration.col_tokens')}</div>
          <div className="v">{tokensValue}</div>
        </div>
      </div>

      <div className="mb-npc-gap-sm flex flex-wrap items-center gap-npc-gap-sm text-npc-meta text-npc-ink-soft">
        <CapabilityMark active={profile.supports_vision} icon={<Eye size={14} />} />
        <CapabilityMark active={profile.supports_reasoning} icon={<Brain size={14} />} />
      </div>

      <div className="model-library-actions border-t border-npc-border-soft pt-npc-gap-sm">
        <button
          className={compactAction}
          disabled={busy || !profile.enabled || !canDirectlyTest}
          onClick={testModel}
        >
          <RefreshCw size={13} aria-hidden="true" />
          {testLabel}
        </button>
        {canManage ? (
          <button className={compactAction} onClick={onEdit}>
            <Pencil size={13} aria-hidden="true" />
            {t('model_configuration.edit_model')}
          </button>
        ) : null}
        {canManage && !profile.platform_managed ? (
          <>
            <button className={compactAction} disabled={busy} onClick={toggle}>
              {profile.enabled ? <Pause size={13} aria-hidden="true" /> : null}
              {profile.enabled
                ? t('model_configuration.disable_model')
                : t('model_configuration.enable_model')}
            </button>
            <button
              className={cn(compactAction, 'text-npc-red-ink')}
              disabled={busy}
              onClick={() => onSafety('delete')}
            >
              <Trash2 size={13} aria-hidden="true" />
              {t('model_configuration.delete_model')}
            </button>
          </>
        ) : null}
      </div>

      {localError ? (
        <p role="alert" className="mb-0 mt-npc-gap-sm text-npc-caption text-npc-red-ink">
          {localError}
        </p>
      ) : null}
    </article>
  )
}

function ModelIdentity({ profile }: { profile: ModelProfile }) {
  const failureHint = modelFailureHint(profile)
  return (
    <div className="min-w-0">
      <div className="truncate text-npc-body font-semibold text-npc-ink">
        {profile.display_name}
      </div>
      <code className="mt-npc-gap-xs block truncate font-npc-mono text-npc-meta text-npc-ink-soft">
        {profile.model_id}
      </code>
      {failureHint ? (
        <span className="mt-npc-gap-xs block text-npc-meta text-npc-red-ink">{failureHint}</span>
      ) : null}
    </div>
  )
}

function CapabilityMark({ active, icon }: { active: boolean; icon: ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-npc-gap-xs text-npc-meta',
        active ? 'text-npc-ink-medium' : 'text-npc-ink-softer',
      )}
      title={active ? t('model_configuration.yes_value') : t('model_configuration.no_value')}
    >
      {icon}
      <span>{active ? <Check size={12} aria-hidden="true" /> : '—'}</span>
    </span>
  )
}

function ModelDrawer({
  profile,
  providers,
  connections,
  profiles,
  projectId,
  requestedProviderID,
  onClose,
}: {
  profile: ModelProfile | null
  providers: readonly ModelProvider[]
  connections: readonly ModelConnection[]
  profiles: readonly ModelProfile[]
  projectId?: string
  requestedProviderID?: string
  onClose: () => void
}) {
  const editing = profile !== null
  const initialTab = profile?.provider === 'custom' ? 'custom' : 'provider'
  const initialProvider =
    !profile &&
    requestedProviderID &&
    providers.some((provider) => provider.id === requestedProviderID)
      ? requestedProviderID
      : profile?.provider && profile.provider !== 'custom'
        ? profile.provider
        : (providers.find((provider) => provider.id !== 'custom')?.id ?? '')
  const initialProviderPreset = providers.find((provider) => provider.id === initialProvider)
  const initialCatalog = initialProviderPreset?.catalog_models ?? []
  const initialConnection = profile
    ? connections.find((connection) => connection.id === profile.connection_id)
    : connections.find(
        (connection) => connection.provider === initialProvider && connection.status !== 'disabled',
      )
  const [tab, setTab] = useState<'provider' | 'custom'>(initialTab)
  const [providerID, setProviderID] = useState(initialProvider)
  const [connectionID, setConnectionID] = useState(initialConnection?.id ?? '')
  const [displayName, setDisplayName] = useState(profile?.display_name ?? '')
  const [modelID, setModelID] = useState(profile?.model_id ?? '')
  const [protocol, setProtocol] = useState<ModelConfigProtocol>(
    initialConnection?.protocol === 'anthropic_messages' ||
      (!initialConnection && initialProviderPreset?.protocol === 'anthropic_messages')
      ? 'anthropic_messages'
      : 'openai_compatible',
  )
  const [baseURL, setBaseURL] = useState(providerBaseURL(initialProviderPreset, initialConnection))
  const [apiKey, setAPIKey] = useState('')
  const [appID, setAppID] = useState('')
  const [maxInput, setMaxInput] = useState(
    profile?.max_input_tokens ? String(profile.max_input_tokens) : '',
  )
  const [maxOutput, setMaxOutput] = useState(
    profile?.max_output_tokens ? String(profile.max_output_tokens) : '',
  )
  const [vision, setVision] = useState(profile?.supports_vision ?? false)
  const [reasoning, setReasoning] = useState(profile?.supports_reasoning ?? false)
  const [reasoningEnabled, setReasoningEnabled] = useState(profile?.reasoning_enabled ?? false)
  const [description, setDescription] = useState(profile?.description ?? '')
  const [discovered, setDiscovered] = useState<DiscoveredModel[]>(
    profile
      ? [
          {
            model_id: profile.model_id,
            ...(profile.max_input_tokens ? { max_input_tokens: profile.max_input_tokens } : {}),
            ...(profile.max_output_tokens ? { max_output_tokens: profile.max_output_tokens } : {}),
            supports_vision: profile.supports_vision,
            supports_reasoning: profile.supports_reasoning,
          },
        ]
      : [],
  )
  const [manualCatalogModel, setManualCatalogModel] = useState(
    Boolean(profile && initialCatalog.length > 0 && !initialCatalog.includes(profile.model_id)),
  )
  const [connectionReady, setConnectionReady] = useState(initialConnection?.status === 'verified')
  const [temporaryConnectionID, setTemporaryConnectionID] = useState<string | null>(null)
  const [savedTemporary, setSavedTemporary] = useState(false)
  const [error, setError] = useState('')

  // 「贴一段代码，我帮你填」。草稿只填进表单，不建任何东西。
  const [assistOpen, setAssistOpen] = useState(false)
  const [assistText, setAssistText] = useState('')
  const [assistNotes, setAssistNotes] = useState<readonly { code: string }[]>([])
  const draftConnection = useDraftModelConnection()

  const applyDraft = async () => {
    if (!assistText.trim()) return
    const result = await draftConnection.mutateAsync(assistText)
    const draft = result.draft
    // 认出来的才填。**认不出的那一格不动**——把它清空等于把他已经填好的东西
    // 拿走，而他贴那段是想省事，不是想重来。
    if (draft.base_url) {
      setBaseURL(draft.base_url)
      setConnectionReady(false)
    }
    if (draft.model_id) setModelID(draft.model_id)
    if (draft.display_name && !displayName) setDisplayName(draft.display_name)
    if (draft.provider && providers.some((item) => item.id === draft.provider)) {
      setProviderID(draft.provider)
    }
    setAssistNotes(draft.notes ?? [])
  }

  const createConnection = useCreateModelConnection()
  const updateConnection = useUpdateModelConnection()
  const deleteConnection = useDeleteModelConnection()
  const createProfile = useCreateModelProfile()
  const updateProfile = useUpdateModelProfile()
  const probeProfile = useProbeModelProfile()
  const discoverModels = useDiscoverModels()

  const selectedProvider = providers.find((provider) => provider.id === providerID)
  const providerCatalog = selectedProvider?.catalog_models ?? []
  const selectedConnection = connections.find((connection) => connection.id === connectionID)
  const sharedCount = connectionID
    ? profiles.filter((candidate) => candidate.connection_id === connectionID).length
    : 0
  const busy =
    createConnection.isPending ||
    updateConnection.isPending ||
    createProfile.isPending ||
    updateProfile.isPending ||
    probeProfile.isPending ||
    discoverModels.isPending

  const chooseProvider = (nextProviderID: string) => {
    const provider = providers.find((item) => item.id === nextProviderID)
    const connection = connections.find(
      (item) => item.provider === nextProviderID && item.status !== 'disabled',
    )
    setProviderID(nextProviderID)
    setConnectionID(connection?.id ?? '')
    setBaseURL(providerBaseURL(provider, connection))
    setProtocol(
      connection?.protocol === 'anthropic_messages'
        ? 'anthropic_messages'
        : connection?.protocol === 'openai_compatible'
          ? 'openai_compatible'
          : (provider?.protocol ?? 'openai_compatible'),
    )
    setAPIKey('')
    setAppID('')
    const nextCatalog = provider?.catalog_models ?? []
    const editingSameConnection = profile && connection?.id === profile.connection_id
    setDiscovered(
      provider?.can_discover
        ? editingSameConnection
          ? [{ model_id: profile.model_id }]
          : []
        : nextCatalog.map((model_id) => ({ model_id })),
    )
    setManualCatalogModel(
      Boolean(
        editingSameConnection && nextCatalog.length > 0 && !nextCatalog.includes(profile.model_id),
      ),
    )
    setModelID(editingSameConnection ? profile.model_id : '')
    setConnectionReady(connection?.status === 'verified')
    setError('')
  }

  const close = async () => {
    if (temporaryConnectionID && !savedTemporary) {
      try {
        await deleteConnection.mutateAsync(temporaryConnectionID)
      } catch {
        // No profile points at this temporary connection. The server never returns its Key.
      }
    }
    onClose()
  }

  const persistProviderConnection = async (): Promise<string | null> => {
    if (!selectedProvider || selectedProvider.adapter_state === 'requires_adapter') {
      setError(t('model_configuration.ui_062'))
      return null
    }
    const credentialPresent =
      selectedConnection?.credential_present || temporaryConnectionID === connectionID
    const speechProvider = selectedProvider.id === 'volcengine_openspeech'
    const secret = speechSecret(appID, apiKey)
    if (!baseURL.trim() || (!credentialPresent && !secret && !speechProvider)) {
      setError(t('model_configuration.form_required'))
      return null
    }
    let id = connectionID
    try {
      if (id) {
        const body: {
          base_url?: string
          api_key?: string
          protocol?: ModelConfigProtocol
        } = {}
        if (baseURL.trim() !== selectedConnection?.base_url) body.base_url = baseURL.trim()
        if (secret) body.api_key = secret
        if (protocol !== selectedConnection?.protocol) body.protocol = protocol
        if (Object.keys(body).length > 0) {
          await updateConnection.mutateAsync({ connectionId: id, body })
        }
      } else {
        const duplicateCount = connections.filter(
          (connection) => connection.provider === selectedProvider.id,
        ).length
        const result = await createConnection.mutateAsync({
          name: `${selectedProvider.name} ${t('model_configuration.ui_015')}${
            duplicateCount > 0 ? ` ${duplicateCount + 1}` : ''
          }`,
          provider: selectedProvider.id,
          protocol: selectedProvider.protocol,
          base_url: baseURL.trim(),
          api_key: secret,
        })
        id = result.connection.id
        setConnectionID(id)
        setTemporaryConnectionID(id)
      }
      setAPIKey('')
      setAppID('')
      return id
    } catch {
      setConnectionReady(false)
      setError(t('model_configuration.connection_failed'))
      return null
    }
  }

  const fetchSupportedModels = async () => {
    setError('')
    if (!selectedProvider?.can_discover) {
      const models = providerCatalog.map((model_id) => ({ model_id }))
      setDiscovered(models)
      // A public provider catalog is not proof that this account's plan can
      // run its first row. Never auto-select a model before the user chooses
      // one and the profile probe verifies it.
      if (!modelID || !models.some((model) => model.model_id === modelID)) {
        setManualCatalogModel(false)
        setModelID('')
      }
      return
    }
    const id = await persistProviderConnection()
    if (!id) return
    try {
      const result = await discoverModels.mutateAsync(id)
      const models = normalizeDiscoveredModels(result.models)
      setDiscovered(models)
      if (!modelID || !models.some((model) => model.model_id === modelID)) setModelID('')
      if (models.length === 0) setError(t('model_configuration.discovery_empty'))
      setConnectionReady(models.length > 0)
    } catch {
      setConnectionReady(false)
      setError(t('model_configuration.connection_failed'))
    }
  }

  const applyDiscoveredModel = (nextModelID: string) => {
    const previousModelID = modelID
    const model = discovered.find((candidate) => candidate.model_id === nextModelID)
    setModelID(nextModelID)
    if (
      !displayName.trim() ||
      displayName === previousModelID ||
      displayName === profile?.model_id
    ) {
      setDisplayName(nextModelID)
    }
    if (!model) return
    setMaxInput(model.max_input_tokens ? String(model.max_input_tokens) : '')
    setMaxOutput(model.max_output_tokens ? String(model.max_output_tokens) : '')
    setVision(model.supports_vision ?? false)
    const supportsReasoning = model.supports_reasoning ?? false
    setReasoning(supportsReasoning)
    if (!supportsReasoning) setReasoningEnabled(false)
  }

  const profileBody = (targetConnectionID: string) => ({
    connection_id: targetConnectionID,
    ...(projectId ? { project_id: projectId } : {}),
    display_name: displayName.trim() || modelID.trim(),
    model_id: modelID.trim(),
    ...(parseTokenLimit(maxInput) ? { max_input_tokens: parseTokenLimit(maxInput) } : {}),
    ...(parseTokenLimit(maxOutput) ? { max_output_tokens: parseTokenLimit(maxOutput) } : {}),
    supports_vision: vision,
    supports_reasoning: reasoning,
    reasoning_enabled: reasoning && reasoningEnabled,
    description: description.trim(),
  })

  const saveProfileAndTest = async (targetConnectionID: string): Promise<boolean> => {
    const body = profileBody(targetConnectionID)
    const result = profile
      ? await updateProfile.mutateAsync({ profileId: profile.id, body })
      : await createProfile.mutateAsync(body)
    if (targetConnectionID === temporaryConnectionID) setSavedTemporary(true)
    const skipProbe = selectedProvider?.can_test === false
    if (skipProbe) {
      onClose()
      return true
    }
    let tested: Awaited<ReturnType<typeof probeProfile.mutateAsync>>
    try {
      tested = await probeProfile.mutateAsync(result.profile.id)
    } catch {
      setError(t('model_configuration.model_saved_test_failed'))
      return false
    }
    if (tested.profile.status !== 'available') {
      setError(modelFailureHint(tested.profile) || t('model_configuration.model_saved_test_failed'))
      return false
    }
    onClose()
    return true
  }

  const submitProvider = async (event: FormEvent) => {
    event.preventDefault()
    setError('')
    if (!modelID.trim()) {
      setError(t('model_configuration.form_required'))
      return
    }
    let id = connectionID
    const configurationDirty =
      !selectedConnection ||
      Boolean(apiKey.trim()) ||
      baseURL.trim() !== selectedConnection.base_url ||
      protocol !== selectedConnection.protocol
    if (!id || configurationDirty) {
      const persistedID = await persistProviderConnection()
      if (!persistedID) return
      id = persistedID
    }
    try {
      await saveProfileAndTest(id)
    } catch {
      setError(t('model_configuration.ui_105'))
    }
  }

  const submitCustom = async (event: FormEvent) => {
    event.preventDefault()
    setError('')
    if (!displayName.trim() || !modelID.trim() || !baseURL.trim()) {
      setError(t('model_configuration.form_required'))
      return
    }
    try {
      const id = profile?.connection_id ?? ''
      if (profile) {
        const connection = connections.find((item) => item.id === profile.connection_id)
        if (!connection) throw new Error('connection_missing')
        if (!connection.credential_present && !apiKey.trim()) {
          setError(t('model_configuration.form_required'))
          return
        }
        const body: {
          base_url?: string
          api_key?: string
          protocol?: ModelConfigProtocol
        } = {}
        if (baseURL.trim() !== connection.base_url) body.base_url = baseURL.trim()
        if (apiKey.trim()) body.api_key = apiKey.trim()
        if (protocol !== connection.protocol) body.protocol = protocol
        if (Object.keys(body).length > 0) {
          await updateConnection.mutateAsync({ connectionId: connection.id, body })
        }
      } else {
        if (!apiKey.trim()) {
          setError(t('model_configuration.form_required'))
          return
        }
        const result = await createConnection.mutateAsync({
          name: `${displayName.trim()} ${t('model_configuration.ui_015')}`,
          provider: 'custom',
          protocol,
          base_url: baseURL.trim(),
          api_key: apiKey.trim(),
          display_name: displayName.trim(),
          model_id: modelID.trim(),
          ...(parseTokenLimit(maxInput) ? { max_input_tokens: parseTokenLimit(maxInput) } : {}),
          ...(parseTokenLimit(maxOutput) ? { max_output_tokens: parseTokenLimit(maxOutput) } : {}),
          supports_vision: vision,
          supports_reasoning: reasoning,
          reasoning_enabled: reasoning && reasoningEnabled,
          description: description.trim(),
        })
        if (!result.profile) throw new Error('profile_missing')
        let tested: Awaited<ReturnType<typeof probeProfile.mutateAsync>>
        try {
          tested = await probeProfile.mutateAsync(result.profile.id)
        } catch {
          setError(t('model_configuration.model_saved_test_failed'))
          return
        }
        if (tested.profile.status !== 'available') {
          setError(t('model_configuration.model_saved_test_failed'))
          return
        }
        onClose()
        return
      }
      await saveProfileAndTest(id)
    } catch {
      setError(t('model_configuration.ui_105'))
    }
  }

  return (
    <Drawer
      title={
        editing
          ? t('model_configuration.edit_model_information')
          : t('model_configuration.add_model')
      }
      onClose={close}
    >
      {!editing ? (
        <div
          className="mb-npc-card grid grid-cols-2 rounded-npc-button bg-npc-surface p-npc-gap-xs"
          role="tablist"
        >
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'provider'}
            onClick={() => setTab('provider')}
            className={tabClass(tab === 'provider')}
          >
            {t('model_configuration.provider_access')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'custom'}
            onClick={() => setTab('custom')}
            className={tabClass(tab === 'custom')}
          >
            {t('model_configuration.custom_access')}
          </button>
        </div>
      ) : null}

      {tab === 'provider' ? (
        <form className="grid gap-npc-card" onSubmit={submitProvider}>
          <div className="grid gap-npc-el rounded-npc-card border border-npc-line bg-npc-warm p-npc-card">
            <button
              type="button"
              data-testid="model-assist-toggle"
              onClick={() => setAssistOpen((open) => !open)}
              className="justify-self-start text-npc-caption text-npc-green"
            >
              {t('model_configuration.assist.entry')}
            </button>
            {assistOpen ? (
              <>
                <p className="text-npc-caption-sm text-npc-t3">
                  {t('model_configuration.assist.hint')}
                </p>
                <textarea
                  className={fieldClass}
                  rows={6}
                  value={assistText}
                  onChange={(event) => setAssistText(event.target.value)}
                  aria-label={t('model_configuration.assist.entry')}
                  data-testid="model-assist-text"
                />
                <button
                  type="button"
                  data-testid="model-assist-apply"
                  disabled={!assistText.trim() || draftConnection.isPending}
                  onClick={() => {
                    void applyDraft()
                  }}
                  className="justify-self-start rounded-npc-btn bg-npc-green px-3 py-1.5 text-npc-caption text-npc-panel disabled:opacity-50"
                >
                  {t('model_configuration.assist.entry')}
                </button>
                {assistNotes.length > 0 ? (
                  <ul data-testid="model-assist-notes" className="grid gap-npc-el-sm">
                    {assistNotes.map((note) => (
                      <li key={note.code} className="text-npc-caption-sm text-npc-t3">
                        {t(note.code as VarlessCopyId)}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </>
            ) : null}
          </div>

          <Field label={t('model_configuration.provider_label')} required>
            <select
              className={fieldClass}
              value={providerID}
              onChange={(event) => chooseProvider(event.target.value)}
              aria-label={t('model_configuration.provider_label')}
              disabled={Boolean(profile?.platform_managed)}
            >
              {providers
                .filter((provider) => provider.id !== 'custom')
                .map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {provider.name}
                    {provider.adapter_state === 'requires_adapter'
                      ? ` · ${t('model_configuration.ui_062')}`
                      : ''}
                  </option>
                ))}
            </select>
          </Field>

          <Field label={t('model_configuration.api_url_label')} required>
            <input
              className={fieldClass}
              value={baseURL}
              onChange={(event) => {
                setBaseURL(event.target.value)
                setConnectionReady(false)
              }}
              aria-label={t('model_configuration.api_url_label')}
              placeholder={selectedProvider?.base_url_hint || 'https://api.example.com/v1'}
            />
          </Field>

          {selectedProvider?.id === 'volcengine_openspeech' ? (
            <Field label={t('model_configuration.app_id_label')}>
              <input
                className={fieldClass}
                value={appID}
                onChange={(event) => setAppID(event.target.value)}
                aria-label={t('model_configuration.app_id_label')}
                placeholder={t('model_configuration.app_id_placeholder')}
              />
            </Field>
          ) : null}

          <Field
            label={
              selectedProvider?.id === 'volcengine_openspeech'
                ? t('model_configuration.access_token_label')
                : t('model_configuration.api_key_label')
            }
            required={
              selectedProvider?.id !== 'volcengine_openspeech' &&
              !selectedConnection?.credential_present
            }
          >
            <input
              className={fieldClass}
              type="password"
              autoComplete="new-password"
              value={apiKey}
              onChange={(event) => {
                setAPIKey(event.target.value)
                setConnectionReady(false)
              }}
              aria-label={
                selectedProvider?.id === 'volcengine_openspeech'
                  ? t('model_configuration.access_token_label')
                  : t('model_configuration.api_key_label')
              }
              placeholder={
                selectedConnection?.credential_present
                  ? '••••••••'
                  : t('model_configuration.api_key_placeholder')
              }
            />
            {selectedConnection?.credential_present ? (
              <FieldHint>{t('model_configuration.api_key_replace_hint')}</FieldHint>
            ) : null}
            {selectedProvider?.auth_hint ? (
              <FieldHint>{selectedProvider.auth_hint}</FieldHint>
            ) : null}
          </Field>

          {sharedCount > 1 ? (
            <p className="m-0 rounded-npc-chip border border-npc-amber-border bg-npc-amber-bg p-npc-grid-sm text-npc-caption text-npc-amber-ink">
              {t('model_configuration.connection_shared_notice', {
                count: String(sharedCount),
              })}
            </p>
          ) : null}

          {selectedProvider?.can_discover ? (
            <div className="grid gap-npc-gap-sm rounded-npc-card border border-npc-border bg-npc-surface p-npc-card-sm">
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={fetchSupportedModels}
              >
                <RefreshCw size={14} aria-hidden="true" />
                {discovered.length > 1
                  ? t('model_configuration.fetch_models_again')
                  : t('model_configuration.fetch_models')}
              </Button>
              {connectionReady ? (
                <p className="m-0 text-npc-caption text-npc-green-bright">
                  {t('model_configuration.connection_ready')}
                </p>
              ) : null}
            </div>
          ) : providerCatalog.length > 0 ? (
            <div className="rounded-npc-card border border-npc-border bg-npc-surface p-npc-card-sm">
              <p className="m-0 text-npc-caption text-npc-ink-soft">
                {t('model_configuration.catalog_loaded')}
              </p>
            </div>
          ) : null}

          <Field label={t('model_configuration.select_model_label')} required>
            {selectedProvider?.can_discover ? (
              <select
                className={fieldClass}
                value={modelID}
                onChange={(event) => applyDiscoveredModel(event.target.value)}
                aria-label={t('model_configuration.select_model_label')}
                disabled={!connectionReady || discovered.length === 0}
              >
                <option value="">{t('model_configuration.supported_model_placeholder')}</option>
                {discovered.map((model) => (
                  <option key={model.model_id} value={model.model_id}>
                    {model.model_id}
                  </option>
                ))}
              </select>
            ) : providerCatalog.length > 0 ? (
              <>
                <select
                  className={fieldClass}
                  value={manualCatalogModel ? '__manual__' : modelID}
                  onChange={(event) => {
                    if (event.target.value === '__manual__') {
                      setManualCatalogModel(true)
                      setModelID('')
                      return
                    }
                    setManualCatalogModel(false)
                    setModelID(event.target.value)
                    if (!displayName.trim() || displayName === profile?.model_id) {
                      setDisplayName(event.target.value)
                    }
                  }}
                  aria-label={t('model_configuration.select_model_label')}
                  disabled={discovered.length === 0}
                >
                  <option value="">{t('model_configuration.supported_model_placeholder')}</option>
                  {discovered.map((model) => (
                    <option key={model.model_id} value={model.model_id}>
                      {model.model_id}
                    </option>
                  ))}
                  <option value="__manual__">{t('model_configuration.manual_model_toggle')}</option>
                </select>
                {manualCatalogModel ? (
                  <input
                    className={fieldClass}
                    value={modelID}
                    onChange={(event) => setModelID(event.target.value)}
                    aria-label={t('model_configuration.model_id_label')}
                    placeholder="model-id"
                  />
                ) : null}
                {selectedProvider?.catalog_hint ? (
                  <FieldHint>{selectedProvider.catalog_hint}</FieldHint>
                ) : null}
              </>
            ) : (
              <>
                <input
                  className={fieldClass}
                  value={modelID}
                  onChange={(event) => setModelID(event.target.value)}
                  aria-label={t('model_configuration.model_id_label')}
                  placeholder="model-id"
                />
                <FieldHint>
                  {selectedProvider?.catalog_hint ||
                    t('model_configuration.discovery_not_supported')}
                </FieldHint>
              </>
            )}
          </Field>

          <Field label={t('model_configuration.display_name_label')}>
            <input
              className={fieldClass}
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              aria-label={t('model_configuration.display_name_label')}
              placeholder={modelID || t('model_configuration.display_name_label')}
            />
          </Field>

          <CapabilityFields
            maxInput={maxInput}
            maxOutput={maxOutput}
            vision={vision}
            reasoning={reasoning}
            reasoningEnabled={reasoningEnabled}
            description={description}
            onMaxInput={setMaxInput}
            onMaxOutput={setMaxOutput}
            onVision={setVision}
            onReasoning={(value) => {
              setReasoning(value)
              if (!value) setReasoningEnabled(false)
            }}
            onReasoningEnabled={setReasoningEnabled}
            onDescription={setDescription}
          />
          {connectionReady ? (
            <FieldHint>{t('model_configuration.discovery_metadata_hint')}</FieldHint>
          ) : null}

          {error ? <FormError>{error}</FormError> : null}
          <DrawerActions onCancel={close} busy={busy} editing={editing} />
        </form>
      ) : (
        <form className="grid gap-npc-card" onSubmit={submitCustom}>
          <Field label={t('model_configuration.display_name_label')} required>
            <input
              className={fieldClass}
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              aria-label={t('model_configuration.display_name_label')}
              placeholder={t('model_configuration.display_name_label')}
            />
          </Field>
          <Field label={t('model_configuration.model_id_label')} required>
            <input
              className={fieldClass}
              value={modelID}
              onChange={(event) => setModelID(event.target.value)}
              aria-label={t('model_configuration.model_id_label')}
              placeholder="model-id"
            />
          </Field>
          <Field label={t('model_configuration.protocol_label')} required>
            <select
              className={fieldClass}
              value={protocol}
              onChange={(event) => setProtocol(event.target.value as ModelConfigProtocol)}
              aria-label={t('model_configuration.protocol_label')}
            >
              <option value="openai_compatible">OpenAI-compatible Chat API</option>
              <option value="anthropic_messages">Anthropic Messages API</option>
            </select>
          </Field>
          <Field label={t('model_configuration.api_url_label')} required>
            <input
              className={fieldClass}
              value={baseURL}
              onChange={(event) => setBaseURL(event.target.value)}
              aria-label={t('model_configuration.api_url_label')}
              placeholder="https://api.example.com/v1"
            />
          </Field>
          <Field
            label={t('model_configuration.api_key_label')}
            required={!initialConnection?.credential_present}
          >
            <input
              className={fieldClass}
              type="password"
              autoComplete="new-password"
              value={apiKey}
              onChange={(event) => setAPIKey(event.target.value)}
              aria-label={t('model_configuration.api_key_label')}
              placeholder={
                initialConnection?.credential_present
                  ? '••••••••'
                  : t('model_configuration.api_key_placeholder')
              }
            />
            {initialConnection?.credential_present ? (
              <FieldHint>{t('model_configuration.api_key_replace_hint')}</FieldHint>
            ) : null}
          </Field>
          {sharedCount > 1 ? (
            <p className="m-0 rounded-npc-chip border border-npc-amber-border bg-npc-amber-bg p-npc-grid-sm text-npc-caption text-npc-amber-ink">
              {t('model_configuration.connection_shared_notice', {
                count: String(sharedCount),
              })}
            </p>
          ) : null}
          <CapabilityFields
            maxInput={maxInput}
            maxOutput={maxOutput}
            vision={vision}
            reasoning={reasoning}
            reasoningEnabled={reasoningEnabled}
            description={description}
            onMaxInput={setMaxInput}
            onMaxOutput={setMaxOutput}
            onVision={setVision}
            onReasoning={(value) => {
              setReasoning(value)
              if (!value) setReasoningEnabled(false)
            }}
            onReasoningEnabled={setReasoningEnabled}
            onDescription={setDescription}
          />
          {error ? <FormError>{error}</FormError> : null}
          <DrawerActions onCancel={close} busy={busy} editing={editing} />
        </form>
      )}
    </Drawer>
  )
}

function CapabilityFields({
  maxInput,
  maxOutput,
  vision,
  reasoning,
  reasoningEnabled,
  description,
  onMaxInput,
  onMaxOutput,
  onVision,
  onReasoning,
  onReasoningEnabled,
  onDescription,
}: {
  maxInput: string
  maxOutput: string
  vision: boolean
  reasoning: boolean
  reasoningEnabled: boolean
  description: string
  onMaxInput: (value: string) => void
  onMaxOutput: (value: string) => void
  onVision: (value: boolean) => void
  onReasoning: (value: boolean) => void
  onReasoningEnabled: (value: boolean) => void
  onDescription: (value: string) => void
}) {
  return (
    <fieldset className="grid gap-npc-grid-sm border-0 p-0">
      <legend className="mb-npc-gap-sm text-npc-section-sm font-semibold text-npc-ink">
        {t('model_configuration.advanced_label')}
      </legend>
      <div className="grid grid-cols-2 gap-npc-grid-sm max-[600px]:grid-cols-1">
        <TokenField
          label={t('model_configuration.max_input_label')}
          value={maxInput}
          presets={[32000, 64000, 128000, 256000]}
          onChange={onMaxInput}
        />
        <TokenField
          label={t('model_configuration.max_output_label')}
          value={maxOutput}
          presets={[16000, 32000, 64000, 128000]}
          onChange={onMaxOutput}
        />
      </div>
      <div className="grid grid-cols-2 gap-npc-grid-sm max-[600px]:grid-cols-1">
        <CheckField
          label={t('model_configuration.vision_label')}
          checked={vision}
          onChange={onVision}
          icon={<Eye size={16} aria-hidden="true" />}
        />
        <CheckField
          label={t('model_configuration.reasoning_capability_label')}
          checked={reasoning}
          onChange={onReasoning}
          icon={<Brain size={16} aria-hidden="true" />}
        />
      </div>
      <div className="rounded-npc-button border border-npc-border bg-npc-panel px-npc-grid-sm py-npc-gap-sm">
        <ReasoningToggle
          checked={reasoning && reasoningEnabled}
          onChange={onReasoningEnabled}
          disabled={!reasoning}
          label={t('model_configuration.reasoning_default_label')}
          hint={
            reasoning
              ? t('model_configuration.reasoning_default_hint')
              : t('model_configuration.reasoning_unsupported_hint')
          }
        />
      </div>
      <Field label={t('model_configuration.description_label')}>
        <textarea
          className={cn(fieldClass, 'min-h-24 resize-y py-npc-gap-sm')}
          value={description}
          maxLength={500}
          onChange={(event) => onDescription(event.target.value)}
          aria-label={t('model_configuration.description_label')}
        />
      </Field>
    </fieldset>
  )
}

function ReasoningToggle({
  checked,
  disabled,
  label,
  hint,
  onChange,
}: {
  checked: boolean
  disabled: boolean
  label: string
  hint: string
  onChange: (value: boolean) => void
}) {
  return (
    <div className="flex items-start justify-between gap-npc-gap">
      <div className="min-w-0">
        <div className="text-npc-caption font-semibold text-npc-ink-medium">{label}</div>
        <div className="mt-npc-gap-xs text-npc-meta text-npc-ink-soft">{hint}</div>
      </div>
      <button
        type="button"
        role="switch"
        aria-label={label}
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative mt-0.5 h-5 w-[34px] shrink-0 rounded-npc-chip transition-colors',
          checked ? 'bg-npc-green' : 'bg-npc-ink-faint',
          disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer',
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            'absolute top-0.5 size-4 rounded-npc-full bg-npc-panel shadow-npc-card transition-transform',
            checked ? 'translate-x-4' : 'translate-x-0.5',
          )}
        />
      </button>
    </div>
  )
}

function TokenField({
  label,
  value,
  presets,
  onChange,
}: {
  label: string
  value: string
  presets: readonly number[]
  onChange: (value: string) => void
}) {
  return (
    <Field label={label}>
      <input
        className={fieldClass}
        inputMode="numeric"
        value={value}
        onChange={(event) => onChange(event.target.value.replace(/[^0-9]/g, ''))}
        aria-label={label}
        placeholder={t('model_configuration.unknown_value')}
      />
      <div className="mt-npc-gap-sm flex flex-wrap gap-npc-gap-xs">
        {presets.map((preset) => (
          <button
            key={preset}
            type="button"
            className={cn(
              'rounded-npc-chip border px-npc-gap-sm py-npc-gap-xs text-npc-meta',
              value === String(preset)
                ? 'border-npc-green-border bg-npc-green-bg text-npc-green-bright'
                : 'border-npc-border text-npc-ink-medium',
            )}
            onClick={() => onChange(String(preset))}
          >
            {formatTokens(preset)}
          </button>
        ))}
      </div>
    </Field>
  )
}

function CheckField({
  label,
  checked,
  onChange,
  icon,
}: {
  label: string
  checked: boolean
  onChange: (value: boolean) => void
  icon: ReactNode
}) {
  return (
    <label className="flex min-h-12 cursor-pointer items-center gap-npc-gap-sm rounded-npc-button border border-npc-border bg-npc-panel px-npc-grid-sm text-npc-caption text-npc-ink-medium">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4 accent-npc-green"
      />
      {icon}
      <span>{label}</span>
    </label>
  )
}

function DrawerActions({
  onCancel,
  busy,
  editing,
}: {
  onCancel: () => void | Promise<void>
  busy: boolean
  editing: boolean
}) {
  return (
    <div className="-mx-npc-card mt-npc-grid flex gap-npc-gap-sm border-t border-npc-border bg-npc-panel px-npc-card pb-npc-card pt-npc-grid-sm max-[600px]:pb-[calc(var(--spacing-npc-card)+env(safe-area-inset-bottom))]">
      <Button
        className="flex-1"
        variant="secondary"
        disabled={busy}
        onClick={() => void onCancel()}
      >
        {t('lulu_agents.cancel')}
      </Button>
      <Button className="flex-[2]" variant="primary" type="submit" disabled={busy}>
        {editing
          ? t('model_configuration.save_and_test_model')
          : t('model_configuration.add_and_test')}
      </Button>
    </div>
  )
}

function Field({
  label,
  required = false,
  children,
}: {
  label: string
  required?: boolean
  children: ReactNode
}) {
  return (
    <label className="grid gap-npc-gap-xs text-npc-caption font-medium text-npc-ink-medium">
      <span>
        {label}
        {required ? <span className="ml-npc-gap-xs text-npc-red-ink">*</span> : null}
      </span>
      {children}
    </label>
  )
}

function FieldHint({ children }: { children: ReactNode }) {
  return <span className="text-npc-meta font-normal text-npc-ink-soft">{children}</span>
}

function FormError({ children }: { children: ReactNode }) {
  return (
    <p
      role="alert"
      className="m-0 rounded-npc-chip border border-npc-red-border bg-npc-red-bg p-npc-grid-sm text-npc-caption text-npc-red-ink"
    >
      {children}
    </p>
  )
}

function Drawer({
  title,
  children,
  onClose,
}: {
  title: string
  children: ReactNode
  onClose: () => void | Promise<void>
}) {
  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) void onClose()
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-npc-overlay" />
        <Dialog.Content
          className="ai-team-theme fixed inset-y-0 right-0 z-50 w-[min(560px,100vw)] overflow-y-auto border-l border-npc-border bg-npc-panel p-npc-card shadow-npc-modal outline-none max-[600px]:w-full max-[600px]:border-l-0" /* npc-token-allow: approved drawer width from supplied reference */
        >
          <div className="mb-npc-card flex items-center justify-between gap-npc-grid">
            <Dialog.Title className="m-0 text-npc-modal-title font-semibold text-npc-ink">
              {title}
            </Dialog.Title>
            <button
              type="button"
              aria-label={t('ui.close')}
              onClick={() => void onClose()}
              className="rounded-npc-chip p-npc-gap-xs text-npc-ink-soft hover:bg-npc-surface"
            >
              <X size={20} aria-hidden="true" />
            </button>
          </div>
          <Dialog.Description className="sr-only">{title}</Dialog.Description>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function ProfileSafetyDialog({
  action,
  profiles,
  onClose,
}: {
  action: NonNullable<SafetyAction>
  profiles: readonly ModelProfile[]
  onClose: () => void
}) {
  const impact = useModelProfileImpact(action.profile.id)
  const retire = useRetireModelProfile()
  const removeProfile = useDeleteModelProfile()
  const [strategy, setStrategy] = useState<'replace' | 'pause'>('replace')
  const [replacementProfileId, setReplacementProfileId] = useState('')
  const [error, setError] = useState('')
  const busy = retire.isPending || removeProfile.isPending
  const replacements = profiles.filter((profile) => {
    if (profile.id === action.profile.id || !profile.enabled || profile.status !== 'available')
      return false
    if (!action.profile.project_id) return !profile.project_id
    return !profile.project_id || profile.project_id === action.profile.project_id
  })
  const execute = async () => {
    setError('')
    try {
      const hasActiveReferences = Boolean(
        impact.data && (impact.data.impact.system_bindings > 0 || impact.data.impact.agents > 0),
      )
      if (action.kind === 'delete' && !hasActiveReferences) {
        await removeProfile.mutateAsync(action.profile.id)
      } else {
        await retire.mutateAsync({
          profileId: action.profile.id,
          body: {
            action: action.kind,
            strategy: hasActiveReferences ? strategy : 'pause',
            ...(hasActiveReferences && strategy === 'replace'
              ? { replacement_profile_id: replacementProfileId }
              : {}),
          },
        })
      }
      onClose()
    } catch {
      setError(t('model_configuration.ui_142'))
    }
  }
  const hasActiveReferences = Boolean(
    impact.data && (impact.data.impact.system_bindings > 0 || impact.data.impact.agents > 0),
  )
  const blocked =
    impact.isPending ||
    impact.isError ||
    (hasActiveReferences &&
      strategy === 'replace' &&
      (!replacementProfileId || replacements.length === 0))
  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title={`${
        action.kind === 'delete'
          ? t('model_configuration.delete_model')
          : t('model_configuration.disable_model')
      } · ${action.profile.display_name}`}
      description={t('model_configuration.impact_description')}
      className="ai-team-theme"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('lulu_agents.cancel')}
          </Button>
          <Button variant="danger" disabled={busy || blocked} onClick={execute}>
            {action.kind === 'delete'
              ? t('model_configuration.delete_model')
              : t('model_configuration.disable_model')}
          </Button>
        </>
      }
    >
      {impact.isPending ? (
        <p className="text-npc-caption text-npc-ink-soft">
          {t('model_configuration.impact_loading')}
        </p>
      ) : null}
      {impact.data ? (
        <div className="grid gap-npc-grid text-npc-caption text-npc-ink-medium">
          <div className="grid gap-npc-gap-xs">
            <span>
              {t('model_configuration.impact_system_bindings')} {impact.data.impact.system_bindings}
            </span>
            <span>
              {t('model_configuration.impact_agents')} {impact.data.impact.agents}
            </span>
          </div>
          {!hasActiveReferences ? (
            <p className="m-0 rounded-npc-chip border border-npc-green-border bg-npc-green-bg p-npc-grid-sm text-npc-caption text-npc-green-deep">
              {t('model_configuration.impact_clear')}
            </p>
          ) : (
            <div
              className="grid grid-cols-2 gap-npc-gap-sm max-[520px]:grid-cols-1"
              role="group"
              aria-label={t('model_configuration.retire_strategy')}
            >
              <button
                type="button"
                onClick={() => setStrategy('replace')}
                className={cn(
                  'rounded-npc-button border p-npc-grid text-left transition-colors',
                  strategy === 'replace'
                    ? 'border-npc-green-bright bg-npc-green-bg text-npc-ink'
                    : 'border-npc-border bg-npc-panel text-npc-ink-medium hover:border-npc-border-hover',
                )}
              >
                <span className="block font-semibold">
                  {t('model_configuration.retire_replace')}
                </span>
                <span className="mt-npc-gap-xs block text-npc-meta text-npc-ink-soft">
                  {t('model_configuration.retire_replace_hint')}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setStrategy('pause')}
                className={cn(
                  'rounded-npc-button border p-npc-grid text-left transition-colors',
                  strategy === 'pause'
                    ? 'border-npc-amber-border bg-npc-amber-bg text-npc-ink'
                    : 'border-npc-border bg-npc-panel text-npc-ink-medium hover:border-npc-border-hover',
                )}
              >
                <span className="block font-semibold">{t('model_configuration.retire_pause')}</span>
                <span className="mt-npc-gap-xs block text-npc-meta text-npc-ink-soft">
                  {t('model_configuration.retire_pause_hint')}
                </span>
              </button>
            </div>
          )}
          {hasActiveReferences && strategy === 'replace' ? (
            <label className="grid gap-npc-gap-xs">
              <span className="font-semibold text-npc-ink">
                {t('model_configuration.retire_replacement_label')}
              </span>
              <select
                className={fieldClass}
                value={replacementProfileId}
                onChange={(event) => setReplacementProfileId(event.target.value)}
              >
                <option value="">{t('model_configuration.retire_replacement_placeholder')}</option>
                {replacements.map((profile) => (
                  <option key={profile.id} value={profile.id}>
                    {profile.display_name} · {providerLabel(profile.provider)}
                  </option>
                ))}
              </select>
              {replacements.length === 0 ? (
                <span className="text-npc-amber-ink">
                  {t('model_configuration.retire_no_replacement')}
                </span>
              ) : null}
            </label>
          ) : hasActiveReferences ? (
            <span className="text-npc-amber-ink">{t('model_configuration.impact_blocked')}</span>
          ) : null}
        </div>
      ) : null}
      {error ? <FormError>{error}</FormError> : null}
    </Modal>
  )
}

function tabClass(active: boolean) {
  return cn(
    'min-h-10 rounded-npc-chip px-npc-grid text-npc-caption font-semibold transition-colors',
    active
      ? 'border border-npc-border-hover bg-npc-surface-warm text-npc-ink shadow-npc-card'
      : 'border border-transparent text-npc-ink-soft',
  )
}

function parseTokenLimit(value: string): number | undefined {
  const parsed = Number.parseInt(value, 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
}

function formatTokens(value: number | null | undefined): string {
  if (!value) return t('model_configuration.unknown_value')
  if (value >= 1_000_000) return `${Math.round(value / 100_000) / 10}M`
  if (value >= 1_000) return `${Math.round(value / 100) / 10}K`
  return String(value)
}

function speechSecret(appID: string, accessToken: string): string {
  const app = appID.trim()
  const token = accessToken.trim()
  if (app && token) return JSON.stringify({ app_id: app, access_token: token })
  return token
}

function providerLabel(provider: string): string {
  const labels: Record<string, string> = {
    litellm_proxy: 'LiteLLM',
    openai: 'OpenAI',
    anthropic: 'Anthropic',
    deepseek: 'DeepSeek',
    huawei_maas: t('model_runtime.provider.huawei_name'),
    volcengine_ark: t('model_runtime.provider.volcengine_name'),
    volcengine_coding_plan: t('model_runtime.provider.volcengine_coding_plan_name'),
    volcengine_agent_plan: t('model_runtime.provider.volcengine_agent_plan_name'),
    volcengine_openspeech: t('model_runtime.provider.volcengine_openspeech_name'),
    zhipu_glm: t('model_runtime.provider.zhipu_name'),
    zhipu_coding_plan: t('model_runtime.provider.zhipu_coding_plan_name'),
    tencent_tokenhub: t('model_runtime.provider.tencent_tokenhub_name'),
    tencent_token_plan: t('model_runtime.provider.tencent_token_plan_name'),
    tencent_hunyuan: t('model_runtime.provider.tencent_name'),
    aws_bedrock: 'AWS Bedrock',
    custom: t('model_runtime.provider.custom_name'),
  }
  return labels[provider] ?? provider
}

function providerBaseURL(
  provider: ModelProvider | undefined,
  connection: ModelConnection | undefined,
): string {
  return connection?.base_url ?? provider?.default_base_url ?? ''
}

function profileLabel(status: ModelProfile['status']): string {
  const labels: Record<ModelProfile['status'], string> = {
    available: t('model_configuration.status_available'),
    unverified: t('model_configuration.status_unverified'),
    unavailable: t('model_configuration.status_unavailable'),
    rate_limited: t('model_configuration.status_rate_limited'),
    quota_exhausted: t('model_configuration.status_quota_exhausted'),
    disabled: t('model_configuration.status_disabled'),
  }
  return labels[status]
}

function profileTone(status: ModelProfile['status']): TagTone {
  if (status === 'available') return 'ok'
  if (status === 'rate_limited' || status === 'quota_exhausted') return 'warn'
  if (status === 'unavailable') return 'danger'
  return 'neutral'
}

function modelFailureHint(profile: ModelProfile): string {
  if (profile.status === 'rate_limited') return t('model_configuration.failure_rate_limited')
  if (profile.status === 'quota_exhausted') return t('model_configuration.failure_quota_exhausted')
  if (!profile.last_error_code) return ''
  if (
    profile.last_error_code === 'auth_failed' ||
    profile.last_error_code === 'authentication_failed'
  )
    return t('model_configuration.failure_auth')
  if (profile.last_error_code === 'model_not_found')
    return t('model_configuration.failure_model_not_found')
  if (profile.last_error_code === 'network_error') return t('model_configuration.failure_network')
  return t('model_configuration.failure_incompatible')
}

function safeReturnPath(raw: string): string {
  try {
    const url = new URL(raw, window.location.origin)
    return url.origin === window.location.origin && url.pathname.startsWith('/')
      ? `${url.pathname}${url.search}${url.hash}`
      : '/agents'
  } catch {
    return '/agents'
  }
}
